import React, { useState, useEffect } from 'react';
import analyticsService from '../services/analyticsService';
import { TOKEN_PRICING } from '../constants/pricing';
import Icon from '../components/icons/Icon';
import './Billing.css';

/**
 * Billing and Metrics Page
 * Displays token consumption and costs by day, month, and year
 */
const Billing = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month'); // 'day', 'month', 'year'
  const [metricsData, setMetricsData] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (!customDateRange) {
      fetchMetrics(timeRange, currentDate);
    }
  }, [timeRange, currentDate]);

  const fetchMetrics = async (range, date = new Date()) => {
    setLoading(true);
    setError(null);

    try {
      const data = await analyticsService.getTokenUsage(range, date);
      setMetricsData(data);
      
      // Log if data is mock or real
      if (data.isMockData) {
        console.warn('⚠️ Displaying MOCK billing data');
      } else {
        console.log('✅ Displaying REAL billing data from analytics');
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsWithDateRange = async (from, to) => {
    setLoading(true);
    setError(null);

    try {
      const data = await analyticsService.getTokenUsageByDateRange(from, to);
      setMetricsData(data);
      
      if (data.isMockData) {
        console.warn('⚠️ Displaying MOCK billing data');
      } else {
        console.log('✅ Displaying REAL billing data from analytics');
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    switch (timeRange) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    switch (timeRange) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setCustomDateRange(false);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    setCustomDateRange(false);
    setCurrentDate(new Date());
  };

  const toggleCustomDateRange = () => {
    setCustomDateRange(!customDateRange);
    if (!customDateRange) {
      // Set default dates when enabling custom range
      const today = new Date();
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      setFromDate(lastMonth.toISOString().split('T')[0]);
      setToDate(today.toISOString().split('T')[0]);
    }
  };

  const formatCurrentPeriod = () => {
    if (customDateRange) {
      return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    }
    
    switch (timeRange) {
      case 'day':
        return currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      case 'month':
        return currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      case 'year':
        return currentDate.getFullYear().toString();
      default:
        return '';
    }
  };

  const calculateCost = (inputTokens, outputTokens) => {
    const inputCost = (inputTokens / 1000000) * TOKEN_PRICING.input;
    const outputCost = (outputTokens / 1000000) * TOKEN_PRICING.output;
    return (inputCost + outputCost).toFixed(2);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    switch (timeRange) {
      case 'day':
        // Show just the hour number (0-23)
        return date.getHours().toString();
      case 'month':
        // Show just the day number
        return date.getDate().toString();
      case 'year':
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      default:
        return dateStr;
    }
  };

  const renderSummaryCards = () => {
    if (!metricsData || !metricsData.summary) return null;

    const { summary } = metricsData;
    const totalCost = calculateCost(summary.totalInputTokens || 0, summary.totalOutputTokens || 0);
    const totalTokens = (summary.totalInputTokens || 0) + (summary.totalOutputTokens || 0);

    return (
      <div className="billing-summary-cards">
        <div className="billing-card">
          <div className="billing-card-icon">
            <Icon name="dollar" size={32} />
          </div>
          <div className="billing-card-content">
            <h3>Total Cost</h3>
            <div className="billing-card-value">${totalCost}</div>
            <div className="billing-card-subtitle">
              {timeRange === 'day' && 'Today'}
              {timeRange === 'month' && 'This Month'}
              {timeRange === 'year' && 'This Year'}
            </div>
          </div>
        </div>

        <div className="billing-card">
          <div className="billing-card-icon">
            <Icon name="bar-chart" size={32} />
          </div>
          <div className="billing-card-content">
            <h3>Total Tokens</h3>
            <div className="billing-card-value">{formatNumber(totalTokens)}</div>
            <div className="billing-card-subtitle">
              Input: {formatNumber(summary.totalInputTokens || 0)} | 
              Output: {formatNumber(summary.totalOutputTokens || 0)}
            </div>
          </div>
        </div>

        <div className="billing-card">
          <div className="billing-card-icon">
            <Icon name="message" size={32} />
          </div>
          <div className="billing-card-content">
            <h3>Requests</h3>
            <div className="billing-card-value">{summary.totalRequests || 0}</div>
            <div className="billing-card-subtitle">API Calls</div>
          </div>
        </div>

        <div className="billing-card">
          <div className="billing-card-icon">
            <Icon name="zap" size={32} />
          </div>
          <div className="billing-card-content">
            <h3>Avg. per Request</h3>
            <div className="billing-card-value">
              {formatNumber(Math.round(totalTokens / (summary.totalRequests || 1)))}
            </div>
            <div className="billing-card-subtitle">tokens</div>
          </div>
        </div>
      </div>
    );
  };

  const renderUsageTable = () => {
    if (!metricsData || !metricsData.breakdown) return null;

    // Sort breakdown by date descending (most recent first)
    const sortedBreakdown = [...metricsData.breakdown].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );

    return (
      <div className="billing-table-container">
        <table className="billing-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Total Tokens</th>
              <th>Requests</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {sortedBreakdown.map((entry, index) => {
              const cost = calculateCost(entry.inputTokens, entry.outputTokens);
              const totalTokens = entry.inputTokens + entry.outputTokens;

              return (
                <tr key={index}>
                  <td>{formatDate(entry.date)}</td>
                  <td>{formatNumber(entry.inputTokens)}</td>
                  <td>{formatNumber(entry.outputTokens)}</td>
                  <td><strong>{formatNumber(totalTokens)}</strong></td>
                  <td>{entry.requests}</td>
                  <td><strong>${cost}</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChart = () => {
    if (!metricsData || !metricsData.breakdown || metricsData.breakdown.length === 0) {
      return (
        <div className="billing-chart">
          <h3>Token Usage Over Time</h3>
          <div className="billing-empty-chart">
            <p>No usage data available for this period</p>
          </div>
        </div>
      );
    }

    const maxTokens = Math.max(...metricsData.breakdown.map(e => e.inputTokens + e.outputTokens));
    
    // Ensure we have a valid maxTokens value
    const safeMaxTokens = maxTokens > 0 ? maxTokens : 1;
    const chartHeight = 180; // Match CSS height

    return (
      <div className="billing-chart">
        <h3>Token Usage Over Time</h3>
        <div className="billing-chart-bars">
          {metricsData.breakdown.map((entry, index) => {
            const totalTokens = entry.inputTokens + entry.outputTokens;
            // Calculate actual pixel heights for proper proportions
            const barHeight = totalTokens > 0 ? Math.max(20, (totalTokens / safeMaxTokens) * chartHeight) : 0;
            const inputHeight = totalTokens > 0 ? (entry.inputTokens / totalTokens) * barHeight : 0;
            const outputHeight = totalTokens > 0 ? (entry.outputTokens / totalTokens) * barHeight : 0;

            return (
              <div key={index} className="billing-chart-bar-container">
                {totalTokens > 0 && (
                  <div className="billing-chart-bar-total">
                    {formatNumber(totalTokens)}
                  </div>
                )}
                {totalTokens > 0 ? (
                  <div className="billing-chart-bar" style={{ height: `${barHeight}px` }}>
                    <div 
                      className="billing-chart-bar-input" 
                      style={{ height: `${inputHeight}px` }}
                      title={`Input: ${formatNumber(entry.inputTokens)}`}
                    >
                      {entry.inputTokens > 0 && inputHeight > 20 && (
                        <span className="billing-chart-bar-text">{formatNumber(entry.inputTokens)}</span>
                      )}
                    </div>
                    <div 
                      className="billing-chart-bar-output" 
                      style={{ height: `${outputHeight}px` }}
                      title={`Output: ${formatNumber(entry.outputTokens)}`}
                    >
                      {entry.outputTokens > 0 && outputHeight > 20 && (
                        <span className="billing-chart-bar-text">{formatNumber(entry.outputTokens)}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="billing-chart-bar-empty" title="No usage">
                    <div className="billing-chart-bar-placeholder"></div>
                  </div>
                )}
                <div className="billing-chart-label">
                  {formatDate(entry.date)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="billing-chart-legend">
          <span><span className="legend-color legend-input"></span> Input Tokens</span>
          <span><span className="legend-color legend-output"></span> Output Tokens</span>
        </div>
      </div>
    );
  };

  return (
    <div className="billing-page">
      <div className="billing-header">
        <div>
          <h1>Billing & Usage</h1>
          <p className="billing-subtitle">
            Monitor your token consumption and costs
          </p>
        </div>
        
        <div className="billing-controls">
          <button
            className="billing-refresh-btn"
            onClick={() => customDateRange ? fetchMetricsWithDateRange(fromDate, toDate) : fetchMetrics(timeRange, currentDate)}
            disabled={loading}
            title="Refresh data"
          >
            <Icon name="refresh" size={20} />
          </button>
          
          <div className="billing-time-range-selector">
            <button
              className={timeRange === 'day' && !customDateRange ? 'active' : ''}
              onClick={() => handleTimeRangeChange('day')}
            >
              Today
            </button>
            <button
              className={timeRange === 'month' && !customDateRange ? 'active' : ''}
              onClick={() => handleTimeRangeChange('month')}
            >
              This Month
            </button>
            <button
              className={timeRange === 'year' && !customDateRange ? 'active' : ''}
              onClick={() => handleTimeRangeChange('year')}
            >
              This Year
            </button>
            <button
              className={customDateRange ? 'active' : ''}
              onClick={toggleCustomDateRange}
              title="Custom date range"
            >
              Custom
            </button>
          </div>
        </div>
      </div>

      {customDateRange && (
        <div className="billing-custom-range">
          <div className="billing-date-inputs">
            <div className="billing-date-input-group">
              <label>From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="billing-date-input-group">
              <label>To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <button
              className="billing-apply-btn"
              onClick={() => fetchMetricsWithDateRange(fromDate, toDate)}
              disabled={!fromDate || !toDate || loading}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {!customDateRange && (
        <div className="billing-navigation">
          <button
            className="billing-nav-btn"
            onClick={handlePrevious}
            disabled={loading}
            title={`Previous ${timeRange}`}
          >
            <Icon name="chevron-left" size={20} />
            <span>Previous {timeRange}</span>
          </button>
          
          <div className="billing-current-period">
            {formatCurrentPeriod()}
          </div>
          
          <button
            className="billing-nav-btn"
            onClick={handleNext}
            disabled={loading || new Date(currentDate) >= new Date()}
            title={`Next ${timeRange}`}
          >
            <span>Next {timeRange}</span>
            <Icon name="chevron-right" size={20} />
          </button>
          
          {new Date(currentDate).toDateString() !== new Date().toDateString() && (
            <button
              className="billing-today-btn"
              onClick={handleToday}
              disabled={loading}
            >
              Today
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="billing-loading">
          <div className="billing-spinner"></div>
          <p>Loading usage data...</p>
        </div>
      )}

      {error && (
        <div className="billing-error">
          <p>⚠️ {error}</p>
          <button onClick={() => fetchMetrics(timeRange)}>Retry</button>
        </div>
      )}

      {!loading && !error && metricsData && (
        <>
          {renderSummaryCards()}
          {renderChart()}
          {renderUsageTable()}

          <div className="billing-pricing-info">
            <h3>💡 Pricing Information</h3>
            <p>
              <strong>Input Tokens:</strong> ${TOKEN_PRICING.input} per 1M tokens<br />
              <strong>Output Tokens:</strong> ${TOKEN_PRICING.output} per 1M tokens
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Billing;
